import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	type ArtemisJobFilterInput,
	type ArtemisJobRunSortInput,
	type ArtemisJobSortInput,
	CreateJobDocument,
	type CreateJobInput,
	DeleteJobDocument,
	GetJobDocument,
	GetJobRunsDocument,
	GetJobsDocument,
	PauseJobDocument,
	ResumeJobDocument,
	TriggerJobRunDocument,
	UpdateJobDocument,
	type UpdateJobInput,
} from "@/common/artemis-sdk/graphql";
import type { ArtemisJob, ArtemisJobRun } from "../model";
import { useGqlClient } from "../use-gql-client";
import { buildWhere, toOrder, usePagedList } from "./use-paged-list";

/**
 * Server-side paginated job list for a workspace (across all projects): Next/Prev cursor
 * pagination plus server-side search (name/id), status filter, and sort.
 */
export function useJobsPaged(workspaceId: string) {
	const gqlFetch = useGqlClient();
	return usePagedList<ArtemisJob>({
		baseKey: ["jobs", workspaceId],
		enabled: !!workspaceId,
		initialSort: [{ id: "createdAt", desc: true }],
		fetchPage: async ({ debouncedSearch, filters, sort, window }) => {
			const data = await gqlFetch(GetJobsDocument, {
				workspaceId,
				...window,
				order: toOrder<ArtemisJobSortInput>(sort),
				where: buildWhere(debouncedSearch, ["name", "id"], filters) as
					| ArtemisJobFilterInput
					| undefined,
			});
			const c = data.jobs;
			return {
				nodes: (c?.nodes as ArtemisJob[]) ?? [],
				totalCount: c?.totalCount ?? 0,
				pageInfo: c?.pageInfo ?? { hasNextPage: false, hasPreviousPage: false },
			};
		},
	});
}

export function useJob(jobId: string) {
	const gqlFetch = useGqlClient();
	return useQuery({
		queryKey: ["job", jobId],
		queryFn: async () => {
			const data = await gqlFetch(GetJobDocument, { jobId });
			return data.job as ArtemisJob | null;
		},
		enabled: !!jobId,
	});
}

/** Server-side paginated runs for a job, newest first. */
export function useJobRuns(jobId: string) {
	const gqlFetch = useGqlClient();
	return usePagedList<ArtemisJobRun>({
		baseKey: ["job-runs", jobId],
		enabled: !!jobId,
		initialSort: [{ id: "firedAt", desc: true }],
		fetchPage: async ({ sort, window }) => {
			const data = await gqlFetch(GetJobRunsDocument, {
				jobId,
				...window,
				order: toOrder<ArtemisJobRunSortInput>(sort),
			});
			const c = data.jobRuns;
			return {
				nodes: (c?.nodes as ArtemisJobRun[]) ?? [],
				totalCount: c?.totalCount ?? 0,
				pageInfo: c?.pageInfo ?? { hasNextPage: false, hasPreviousPage: false },
			};
		},
	});
}

export function useCreateJob() {
	const gqlFetch = useGqlClient();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (input: CreateJobInput) =>
			gqlFetch(CreateJobDocument, { input }),
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({
				queryKey: ["jobs", variables.projectId],
			});
		},
	});
}

export function useUpdateJob(jobId: string) {
	const gqlFetch = useGqlClient();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (input: UpdateJobInput) =>
			gqlFetch(UpdateJobDocument, { jobId, input }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["job", jobId] });
			queryClient.invalidateQueries({ queryKey: ["jobs"] });
		},
	});
}

export function usePauseJob() {
	const gqlFetch = useGqlClient();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (jobId: string) => gqlFetch(PauseJobDocument, { jobId }),
		onSuccess: (data) => {
			queryClient.invalidateQueries({
				queryKey: ["job", data.pauseJob.job.id],
			});
			queryClient.invalidateQueries({ queryKey: ["jobs"] });
		},
	});
}

export function useResumeJob() {
	const gqlFetch = useGqlClient();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (jobId: string) => gqlFetch(ResumeJobDocument, { jobId }),
		onSuccess: (data) => {
			queryClient.invalidateQueries({
				queryKey: ["job", data.resumeJob.job.id],
			});
			queryClient.invalidateQueries({ queryKey: ["jobs"] });
		},
	});
}

export function useDeleteJob() {
	const gqlFetch = useGqlClient();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (jobId: string) => gqlFetch(DeleteJobDocument, { jobId }),
		onSuccess: (_, jobId) => {
			queryClient.invalidateQueries({ queryKey: ["job", jobId] });
			queryClient.invalidateQueries({ queryKey: ["jobs"] });
		},
	});
}

export function useTriggerJobRun() {
	const gqlFetch = useGqlClient();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (jobId: string) => gqlFetch(TriggerJobRunDocument, { jobId }),
		onSuccess: (data) => {
			queryClient.invalidateQueries({
				queryKey: ["job-runs", data.triggerJobRun.jobRun.jobId],
			});
		},
	});
}
